import { useQuery } from "@tanstack/react-query";
import { onAuthStateChanged } from "firebase/auth";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { PropsWithChildren, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getParsedTokenPermissions } from "./auth/permissions/permissionsClaims";
import SignIn from "./auth/SignIn";
import { signOut } from "./auth/utils";
import { bugsnagClient } from "./bugsnag";
import UserContext, { UserContextType } from "./context/UserContext";
import {
  isLocalDevelopment,
  VITE_DISABLE_SUPER_ADMIN_IN_CLIENT,
  VITE_MOCK_SERVICE_WORKER,
} from "./env";
import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import { getFirebaseAuth } from "./firebaseConfig";
import { Loader } from "./global_components";
import {
  CURRENT_USER_KEY,
  CurrentUser,
  getCurrentUserInfo,
} from "./global_functions/backpackSdk/auth/currentUser";
import LiveSession from "./livesession";

function useTrackUser(currentUser?: CurrentUser | null) {
  const ldClient = useLDClient();

  useEffect(() => {
    if (currentUser) {
      LiveSession.identifyUser({
        email: currentUser.email,
        name: `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`,
        accountId: `${currentUser.accountId ?? ""}`,
        userId: currentUser.id,
      });

      bugsnagClient?.setUser(
        String(currentUser.id),
        currentUser.email,
        `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`
      );

      bugsnagClient?.addMetadata("user", {
        accountId: currentUser.accountId,
        superAdmin: currentUser.superAdmin,
        permissions: currentUser.userPermissions,
        // `userRole` is the established LaunchDarkly/Bugsnag attribute key - the
        // API renamed the field to `job_title` but the key must stay put
        userRole: currentUser.jobTitle ?? "N/A",
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && ldClient) {
      ldClient.identify({
        kind: "user",
        key: currentUser.id.toString(),
        firstName: currentUser.firstName ?? "N/A",
        lastName: currentUser.lastName ?? "N/A",
        email: currentUser.email,
        avatar: currentUser.imageURL ?? "N/A",
        accountId: currentUser.accountId ?? "N/A",
        superAdmin: currentUser.superAdmin,
        permissions: currentUser.userPermissions,
        // `userRole` is the established LaunchDarkly/Bugsnag attribute key - the
        // API renamed the field to `job_title` but the key must stay put
        userRole: currentUser.jobTitle ?? "N/A",
      });
      bugsnagClient?.addFeatureFlags(
        Object.entries(ldClient.allFlags()).map(([name, variant]) => ({
          name,
          variant: variant !== null ? String(variant) : null,
        }))
      );
    }
  }, [ldClient, currentUser]);
}

const consoleUnauthenticatedBaseRoutes = [
  "/rewards",
  "/print", // PDF service needs to access app
  "/auth/action",
  "/accept-invite",
];

const checkUnauthenticatedRoute = (pathname: string) => {
  return consoleUnauthenticatedBaseRoutes.some((route) =>
    pathname.startsWith(route)
  );
};

export function UserContextProvider({ children }: PropsWithChildren) {
  const location = useLocation();
  const isUnauthenticatedRoute = checkUnauthenticatedRoute(location.pathname);

  const isRoleBasedPermissionsFlagEnabled =
    useFeatureFlag("role-based-permissions", "off") === "v1";

  const {
    data: currentUser,
    isError,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [CURRENT_USER_KEY, isRoleBasedPermissionsFlagEnabled],
    queryFn: async (): Promise<CurrentUser | null> => {
      if (VITE_MOCK_SERVICE_WORKER) {
        const { worker } = await import("./__mocks__/msw/browser");
        await worker.start({
          onUnhandledRequest: "bypass",
        });
      }

      await getFirebaseAuth().authStateReady();

      // get the current Google Identity Platform user
      const { currentUser: authUser } = getFirebaseAuth();
      if (!authUser && !isUnauthenticatedRoute) {
        return null;
      }

      // get the role based permissions from the token claims
      const claims = (await authUser?.getIdTokenResult())?.claims;
      const permissions = getParsedTokenPermissions(claims, {
        isRoleBasedPermissionsFlagEnabled,
      });

      // query the Backpack user with the valid JWT from Google Identity Platform
      // throws if the request fails, surfacing the error via `isError` below
      const currUser = await getCurrentUserInfo();

      if (isLocalDevelopment && VITE_DISABLE_SUPER_ADMIN_IN_CLIENT) {
        currUser.superAdmin = false;
      }

      return { ...currUser, permissions };
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Sign out if the user is no longer authenticated and viewing a page that requires authentication
  useEffect(() => {
    if (!isLoading && !currentUser && !isUnauthenticatedRoute) {
      signOut();
    }
  }, [isLoading, currentUser, isUnauthenticatedRoute]);

  useTrackUser(currentUser);
  const userContextValue = useMemo(
    (): UserContextType => ({
      initialized: true,
      currentUser,
      isLoading,
      refreshCurrentUser: async () => {
        await refetch();
      },
    }),
    [currentUser, refetch, isLoading]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), () => {
      refetch();
    });
    return () => unsubscribe();
  }, [refetch]);

  if (isLoading) {
    return <Loader size={80} />;
  }

  if (!isUnauthenticatedRoute && !currentUser) {
    return <SignIn isCurrentUserError={isError} />;
  }

  return (
    <UserContext.Provider value={userContextValue}>
      {children}
    </UserContext.Provider>
  );
}
